export { metaMaskWallet, walletConnectWallet } from '@rainbow-me/rainbowkit/wallets';
import { getWalletConnectConnector } from '@rainbow-me/rainbowkit';
import { createConnector } from 'wagmi';
import { injected } from 'wagmi/connectors';

// wallets/index.ts
function hasInjectedProvider({ namespace }) {
  if (typeof getWindowProviderNamespace(namespace) !== "undefined") return true;
  return false;
}
function getWindowProviderNamespace(namespace) {
  const providerSearch = (provider, namespace2) => {
    const [property, ...path] = namespace2.split(".");
    const _provider = provider[property];
    if (_provider) {
      if (path.length === 0) return _provider;
      return providerSearch(_provider, path.join("."));
    }
  };
  if (typeof window !== "undefined") return providerSearch(window, namespace);
}
function getInjectedConnector({ namespace }) {
  const provider = getInjectedProvider({ namespace });
  return createInjectedConnector(provider);
}
function getInjectedProvider({ namespace }) {
  const _window = typeof window !== "undefined" ? window : void 0;
  if (typeof _window === "undefined") return;
  if (namespace) {
    const windowProvider = getWindowProviderNamespace(namespace);
    if (windowProvider) return windowProvider;
  }
  const providers = _window.ethereum?.providers;
  if (typeof providers !== "undefined" && providers.length > 0) return providers[0];
  return _window.ethereum;
}
function createInjectedConnector(provider) {
  return (walletDetails) => {
    const injectedConfig = provider ? {
      target: () => ({
        id: walletDetails.rkDetails.id,
        name: walletDetails.rkDetails.name,
        provider
      })
    } : {};
    return createConnector((config) => ({
      // Spread the injectedConfig object, which may be empty or contain the target function
      ...injected(injectedConfig)(config),
      ...walletDetails
    }));
  };
}
function isAndroid() {
  return typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);
}
function isSmallIOS() {
  return typeof navigator !== "undefined" && /iPhone|iPod/.test(navigator.userAgent);
}
function isLargeIOS() {
  return typeof navigator !== "undefined" && (/iPad/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function isIOS() {
  return isSmallIOS() || isLargeIOS();
}
function isMobile() {
  return isAndroid() || isIOS();
}

// wallets/kaikasWallet/kaikasWallet.ts
var kaikasWallet = ({ projectId, walletConnectParameters }) => {
  const isKaikasWalletInjected = hasInjectedProvider({
    namespace: "klaytn"
  });
  const shouldUseWalletConnect = !isKaikasWalletInjected;
  const getUri = () => {
    return `kaikas://wallet/browser?url=${encodeURI(window.location.origin)}`;
  };
  return {
    id: "kaikas",
    name: "Kaikas Wallet",
    iconUrl: async () => (await import('./kaikasWallet-2M7J43ZK.mjs')).default,
    installed: isKaikasWalletInjected || void 0,
    iconBackground: "#fff",
    downloadUrls: {
      chrome: "https://chromewebstore.google.com/detail/kaikas/jblndlipeogpafnldhgmapagcccfchpi",
      browserExtension: "https://app.kaikas.io",
      qrCode: "https://app.kaikas.io",
      ios: "https://apps.apple.com/us/app/kaikas-mobile-crypto-wallet/id1626107061",
      android: "https://play.google.com/store/apps/details?id=io.klutch.wallet",
      mobile: "https://app.kaikas.io"
    },
    mobile: {
      getUri: shouldUseWalletConnect ? getUri : (uri) => `kaikas://wc?uri=${encodeURIComponent(uri)}`
    },
    qrCode: shouldUseWalletConnect ? {
      getUri: (uri) => uri,
      instructions: {
        learnMoreUrl: "https://kaikas.io",
        steps: [
          {
            description: "wallet_connectors.kaikas.qr_code.step1.description",
            step: "install",
            title: "wallet_connectors.kaikas.qr_code.step1.title"
          },
          {
            description: "wallet_connectors.kaikas.qr_code.step2.description",
            step: "create",
            title: "wallet_connectors.kaikas.qr_code.step2.title"
          },
          {
            description: "wallet_connectors.kaikas.qr_code.step3.description",
            step: "refresh",
            title: "wallet_connectors.kaikas.qr_code.step3.title"
          }
        ]
      }
    } : void 0,
    extension: {
      instructions: {
        learnMoreUrl: "https://kaikas.io",
        steps: [
          {
            description: "wallet_connectors.kaikas.extension.step1.description",
            step: "install",
            title: "wallet_connectors.kaikas.extension.step1.title"
          },
          {
            description: "wallet_connectors.kaikas.extension.step2.description",
            step: "create",
            title: "wallet_connectors.kaikas.extension.step2.title"
          },
          {
            description: "wallet_connectors.kaikas.extension.step3.description",
            step: "refresh",
            title: "wallet_connectors.kaikas.extension.step3.title"
          }
        ]
      }
    },
    createConnector: shouldUseWalletConnect ? getWalletConnectConnector({
      projectId,
      walletConnectParameters
    }) : (
      // @ts-ignore
      getInjectedConnector(isMobile() ? { } : { namespace: "klaytn" })
    )
  };
};
var kaiaWallet = ({ projectId, walletConnectParameters }) => {
  const isKaiaWalletInjected = hasInjectedProvider({
    namespace: "klaytn"
  });
  const shouldUseWalletConnect = !isKaiaWalletInjected;
  const getUri = () => {
    return `kaikas://wallet/browser?url=${encodeURI(window.location.origin)}`;
  };
  return {
    id: "kaia",
    name: "Kaia Wallet",
    iconUrl: async () => (await import('./kaiaWallet-U7P5NUQD.mjs')).default,
    installed: isKaiaWalletInjected || void 0,
    iconBackground: "#fff",
    downloadUrls: {
      chrome: "https://chromewebstore.google.com/detail/kaia-wallet/jblndlipeogpafnldhgmapagcccfchpi",
      browserExtension: "https://app.kaiawallet.io",
      qrCode: "https://app.kaiawallet.io",
      ios: "https://apps.apple.com/us/app/kaia-wallet/id6502896387",
      android: "https://play.google.com/store/apps/details?id=io.klutch.wallet",
      mobile: "https://app.kaiawallet.io"
    },
    mobile: {
      getUri: shouldUseWalletConnect ? getUri : (uri) => `kaikas://wc?uri=${encodeURIComponent(uri)}`
    },
    qrCode: shouldUseWalletConnect ? {
      getUri: (uri) => uri,
      instructions: {
        learnMoreUrl: "https://kaiawallet.io",
        steps: [
          {
            description: "wallet_connectors.kaia.qr_code.step1.description",
            step: "install",
            title: "wallet_connectors.kaia.qr_code.step1.title"
          },
          {
            description: "wallet_connectors.kaia.qr_code.step2.description",
            step: "create",
            title: "wallet_connectors.kaia.qr_code.step2.title"
          },
          {
            description: "wallet_connectors.kaia.qr_code.step3.description",
            step: "refresh",
            title: "wallet_connectors.kaia.qr_code.step3.title"
          }
        ]
      }
    } : void 0,
    extension: {
      instructions: {
        learnMoreUrl: "https://kaiawallet.io",
        steps: [
          {
            description: "wallet_connectors.kaia.extension.step1.description",
            step: "install",
            title: "wallet_connectors.kaia.extension.step1.title"
          },
          {
            description: "wallet_connectors.kaia.extension.step2.description",
            step: "create",
            title: "wallet_connectors.kaia.extension.step2.title"
          },
          {
            description: "wallet_connectors.kaia.extension.step3.description",
            step: "refresh",
            title: "wallet_connectors.kaia.extension.step3.title"
          }
        ]
      }
    },
    createConnector: shouldUseWalletConnect ? getWalletConnectConnector({
      projectId,
      walletConnectParameters
    }) : (
      // @ts-ignore
      getInjectedConnector(isMobile() ? { } : { namespace: "klaytn" })
    )
  };
};

export { kaiaWallet, kaikasWallet };
